import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  getAccessContextFromAuth,
  getDenyMessage,
  isAccessPending,
  resolveRouteAccess,
} from '../config/routeAccess';
import type { AccessDecision } from '../config/routeAccessTypes';

export interface RouteAccessState {
  decision: AccessDecision;
  denyMessage: string;
  pathname: string;
}

export function useRouteAccess(): RouteAccessState {
  const location = useLocation();
  const params = useParams();
  const { user, isAuthenticated } = useAuth();

  return useMemo(() => {
    const pathname = location.pathname;
    const pending = isAccessPending(isAuthenticated, user?.roles);
    if (pending) {
      return { decision: 'pending' as const, denyMessage: '', pathname };
    }

    const ctx = getAccessContextFromAuth(pathname, params, user);
    const resolved = resolveRouteAccess(ctx);
    return {
      decision: resolved.decision,
      denyMessage: getDenyMessage(resolved.denyMessageKey),
      pathname,
    };
  }, [isAuthenticated, location.pathname, params, user]);
}
